import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const SearchOverlay = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const inputRef = useRef(null);

  useEffect(() => {
    fetch(`https://rehabwmg.online/api/products?lang=${i18n.language}`)
      .then((res) => res.json())
      .then((data) => setAllProducts(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Fetch error:", err));
  }, [i18n.language]);

  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setResults([]);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }
    const q = query.toLowerCase();
    const filtered = allProducts.filter((p) => {
      const name = p.Name;
      if (typeof name === "object") {
        return (
          (name.fr || "").toLowerCase().includes(q) ||
          (name.ar || "").toLowerCase().includes(q)
        );
      }
      return (name || "").toLowerCase().includes(q);
    });
    setResults(filtered);
  }, [query, allProducts]);

  const slugify = (nameObj) => {
    const name =
      typeof nameObj === "object"
        ? nameObj[i18n.language] || nameObj.fr || ""
        : nameObj || "";
    return name.trim().replace(/\s+/g, "-").toLowerCase();
  };

  const getDisplayName = (nameObj) => {
    if (typeof nameObj === "object") {
      return nameObj[i18n.language] || nameObj.fr || "";
    }
    return nameObj || "";
  };

  const handleSelect = (product) => {
    navigate(`/product-details/${product.ProductID}-${slugify(product.Name)}`);
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        .search-overlay * {
          -webkit-tap-highlight-color: transparent !important;
          outline: none !important;
          user-select: none;
        }
        .search-overlay input {
          user-select: text;
          caret-color: #fff;
        }
        .search-overlay button:focus,
        .search-overlay button:active {
          outline: none !important;
          box-shadow: none !important;
          -webkit-tap-highlight-color: transparent !important;
        }
        .result-item:hover { background: #1a1a1a; }
      `}</style>

      <div
        className="search-overlay"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(0,0,0,0.88)",
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
        }}
      >
        {/* Close button */}
        <div style={{ display: "flex", justifyContent: "flex-end", padding: "16px 24px" }}>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "#fff",
              fontSize: "32px",
              cursor: "pointer",
              lineHeight: 1,
              outline: "none",
              WebkitTapHighlightColor: "transparent",
              padding: "4px 8px",
            }}
          >
            ✕
          </button>
        </div>

        {/* Search input row */}
        <div style={{ display: "flex", alignItems: "stretch" }}>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              i18n.language === "ar"
                ? "ابحث عن منتج..."
                : "Rechercher un produit..."
            }
            dir={i18n.language === "ar" ? "rtl" : "ltr"}
            style={{
              flex: 1,
              background: "transparent",
              border: "none",
              borderBottom: "1px solid #555",
              color: "#fff",
              fontSize: "clamp(20px, 4vw, 32px)",
              padding: "12px 24px",
              outline: "none",
              boxShadow: "none",
              fontFamily: "inherit",
              WebkitTapHighlightColor: "transparent",
            }}
          />
          <button
            onClick={query ? () => setQuery("") : onClose}
            style={{
              background: "#4e7662",
              border: "none",
              color: "#fff",
              width: "80px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "22px",
              outline: "none",
              boxShadow: "none",
              WebkitTapHighlightColor: "transparent",
            }}
          >
            {query ? "✕" : "🔍"}
          </button>
        </div>

        {/* Results */}
        <div style={{ overflowY: "auto", maxHeight: "calc(100vh - 160px)" }}>

          {query && results.length === 0 && (
            <p style={{ color: "#aaa", padding: "16px 24px", fontSize: "15px" }}>
              {i18n.language === "ar" ? "لا توجد نتائج" : "Aucun produit trouvé"}
            </p>
          )}

          {results.length > 0 && (
            <p style={{ color: "#666", padding: "8px 24px", fontSize: "13px", margin: 0 }}>
              {results.length} {i18n.language === "ar" ? "نتيجة" : "résultat(s) trouvé(s)"}
            </p>
          )}

          {results.map((product, index) => (
            <div
              key={index}
              className="result-item"
              onClick={() => handleSelect(product)}
              style={{
                padding: "16px 24px",
                borderBottom: "1px solid #2a2a2a",
                color: "#fff",
                fontSize: "clamp(15px, 2.5vw, 20px)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                transition: "background 0.15s",
              }}
            >
              {product.image && (
                <img
                  src={`https://rehabwmg.online/storage/${product.image}`}
                  alt=""
                  style={{
                    width: "40px",
                    height: "40px",
                    objectFit: "contain",
                    borderRadius: "6px",
                    background: "#fff",
                    flexShrink: 0,
                  }}
                />
              )}
              <span>{getDisplayName(product.Name)}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default SearchOverlay;